﻿import React, { useState, useMemo, useContext } from 'react';
import Context, { context_type } from '@Util/Context';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import Table_Head from '@Elements/Table/Table_Head';
import Table_Data from '@Elements/Table/Table_Data';
import Customer_Name_With_Color from '@Elements/Table/Customer_Name_With_Color';
import { Icon_Edit, Icon_Trash } from '@Elements/ImageElements/Icons';

import {
    str_isEmpty,
    generate_unique_id
} from '@Util/Utils';
import {
    Time_Log_Entry,
    Timelog_Table_Props
} from '@Util/Types';

export default function Timelog_Table(props: Timelog_Table_Props): JSX.Element {
    const [t] = useTranslation();
    const {
        delete_time_object,
        set_prompt_information
    }: context_type = useContext(Context);

    const [sort_direction, set_sort_direction] = useState<"Ascending" | "Descending">("Ascending");
    const [active_sorting_column, set_active_sorting_column] = useState<string>("name");

    const table_data: Time_Log_Entry[] = useMemo(() => {
        let sorted_data: Time_Log_Entry[] = [];

        if (sort_direction == "Ascending") {
            sorted_data = props.logs.sort((a: Time_Log_Entry, b: Time_Log_Entry) => a[active_sorting_column] > b[active_sorting_column] ? 1 : -1);
        } else {
            sorted_data = props.logs.sort((a: Time_Log_Entry, b: Time_Log_Entry) => a[active_sorting_column] < b[active_sorting_column] ? 1 : -1);
        }

        return sorted_data;
    }, [props.logs, sort_direction, active_sorting_column]);

    function handleSetSortingOnTableHead(col: string): void {
        if (str_isEmpty(col)) {
            throw new Error("Handle sorting on table: Column is empty");
        }

        if (col === active_sorting_column) {
            if (sort_direction == "Ascending") {
                return set_sort_direction("Descending");
            } else {
                return set_sort_direction("Ascending");
            }
        }

        set_active_sorting_column(col);
        set_sort_direction("Ascending");
    }

    function Empty_Table(): JSX.Element {
        return (
            <tr>
                <Table_Data align="left">
                    <span className="Table_Data_Customer_Name">{t('Text.NoTimelogObjectsToShow')}</span>
                </Table_Data>
                <Table_Data align="right">
                    <></>
                </Table_Data>
            </tr>
        )
    }

    return (
        <>
            <div className="Table_Wrapper">
                <table className="Table">
                    <thead>
                        <tr key={generate_unique_id()}>
                            <Table_Head
                                align="left"
                                sortDirection={sort_direction}
                                activeSortingColumn={active_sorting_column}
                                text={t('Text.Time')}
                                sortingAction={() => handleSetSortingOnTableHead('startDate')}
                                sortingDataName="startDate"
                                style={{ width: "30%" }}
                                
                            />
                            <Table_Head
                                align="left"
                                sortDirection={sort_direction}
                                activeSortingColumn={active_sorting_column}
                                text={t('Text.CustomerAndProject')}
                                sortingAction={() => handleSetSortingOnTableHead('customerName')}
                                sortingDataName="customerName"
                                style={{ width: "45%" }}
                                
                            />
                            <Table_Head
                                align="right"
                                text={t('Text.Action')}
                                style={{ width: "25%" }}
                            />
                        </tr>
                    </thead>
                    <tbody>
                        {
                            table_data.isEmpty() ?
                                <Empty_Table /> :
                                table_data.map((log: Time_Log_Entry) =>
                                    <tr key={generate_unique_id()}>
                                        <Table_Data
                                            align="left"
                                            className="Table_Data_With_Buttons"
                                            style={{ width: "30%" }}
                                        >
                                            <span>{`${moment(log.startDate).format("HH:mm")} - ${moment(log.endDate).format("HH:mm")}`}</span>
                                        </Table_Data>
                                        <Table_Data
                                            align="left"
                                            className="Table_Data_With_Buttons"
                                            style={{ width: "45%" }}
                                        >
                                            <Customer_Name_With_Color
                                                color={log.color}
                                                name={`${log.customerName} - ${log.projectName}`}
                                            />
                                        </Table_Data>
                                        <Table_Data
                                            align="right"
                                            className="Table_Data_With_Buttons"
                                            style={{ width: "25%" }}
                                        >
                                            <div className="Table_Buttons_Row">
                                                <button
                                                    type="button"
                                                    className="Button_Icon"
                                                    onClick={() => {
                                                        props.set_selected_log_entry(log.id.toString());
                                                        props.set_edit_mode(true);
                                                    }}
                                                >
                                                    <Icon_Edit />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="Button_Icon Button_Icon--RedHover"
                                                    onClick={() => set_prompt_information({
                                                        open: true,
                                                        prompt_headline: `${t('Text.Delete')} ${log.projectName}`,
                                                        prompt_text: t('Text.DeleteTextLogEntry', { project_name: log.projectName }),
                                                        on_accept_action: () => {
                                                            delete_time_object(log.id);
                                                            set_prompt_information({
                                                                open: false,
                                                                prompt_headline: "",
                                                                prompt_text: "",
                                                                on_accept_action: null,
                                                                on_cancel_action: null
                                                            });
                                                        },
                                                        on_cancel_action: () => set_prompt_information({
                                                            open: false,
                                                            prompt_headline: "",
                                                            prompt_text: "",
                                                            on_accept_action: null,
                                                            on_cancel_action: null
                                                        
                                                        })
                                                    })}
                                                >
                                                    <Icon_Trash />
                                                </button>
                                            </div>
                                        </Table_Data>
                                    </tr>
                                )
                        }
                    </tbody>
                </table>
            </div>
        </>
    );
}